/* ═══════════════════════════════════════════════════════════════════════════════
   useDashboardFilteredData: Central filter orchestration hook
   
   Consumes raw data from existing hooks and applies centralized filters
   client-side. Returns filtered, ready-to-render datasets for every
   dashboard section.
   
   Strategy: Fetch ALL data once → filter client-side with useMemo.
   This keeps filter changes instant (< 100ms, no DB round-trips).
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useMemo } from 'react';
import { useDashboardFilter } from '../contexts/useDashboardFilterHooks';
import type { ProjectWithDetails } from './useProjects';
import type {
  VariationsStats,
  ProjectVariationStats,
  MonthlyVariationData,
} from './useVariationsStats';
import type { ClaimsProgressRow } from './useDashboardStats';
import type { DateRange } from '../types/dashboard-filters';
import { isInDateRange } from '../types/dashboard-filters';
import { claimsTrendBreakdown, type TrendClaimRow } from './claimsTrendWindow';

// ─── Return Type ─────────────────────────────────────────────────────────────

export interface FilteredDashboardData {
  /** Projects after applying all filters (project, builder, status). */
  filteredProjects: ProjectWithDetails[];
  /** Set of filtered project IDs for fast lookup. */
  filteredProjectIdSet: Set<string>;
  /** Number of total projects (before filtering). */
  totalProjectCount: number;

  /** Filtered variation stats aggregates. */
  filteredVariationStats: VariationsStats;
  /** Filtered project-level stats array. */
  filteredProjectStats: ProjectVariationStats[];
  /** Filtered monthly variation trend data. */
  filteredMonthlyTrend: MonthlyVariationData[];

  // ── Filtered claims stats (previously from useDashboardStats) ──────────
  /** Sum of contract values from filtered projects. */
  filteredContractValue: number;
  /** Pending variation count from filtered projects. */
  filteredPendingCount: number;
  /** This month claims from filtered projects. */
  filteredThisMonthClaims: number;
  /** Last month claims from filtered projects. */
  filteredLastMonthClaims: number;
  /** Year-to-date claims from filtered projects. */
  filteredYearToDateClaims: number;
  /** Monthly claims breakdown filtered by project. */
  filteredMonthlyBreakdown: { month: number; monthName: string; amount: number }[];

  /** Whether filters are active. */
  isFiltered: boolean;
  /** Human-readable summary. */
  filterSummary: string;
  /** Count of active filters. */
  activeFilterCount: number;
  /** The date range from filter context. */
  dateRange: DateRange;
  /** The active date preset. */
  datePreset: import('../types/dashboard-filters').DatePreset;
}

/** Input type for claims stats raw data. */
export interface ClaimsStatsInput {
  rawClaimsProgress: ClaimsProgressRow[];
  subcatToProject: Record<string, string>;
}

// ─── Monthly Data Filtering ──────────────────────────────────────────────────

function filterMonthlyVariationData(
  data: MonthlyVariationData[],
  dateRange: DateRange,
): MonthlyVariationData[] {
  return data.filter((d) => isInDateRange(d.monthNum, d.year, dateRange));
}

// ─── Recalculate Variation Aggregates ────────────────────────────────────────

function recalcVariationStats(
  projectStats: ProjectVariationStats[],
  monthlyTrend: MonthlyVariationData[],
  _dateRange: DateRange,
): VariationsStats {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  // Aggregate from filtered project stats
  let totalVariance = 0;
  let totalCredit = 0;
  let totalApproved = 0;
  let totalPending = 0;

  for (const ps of projectStats) {
    totalVariance += ps.varianceTotal;
    totalCredit += ps.creditTotal;
    totalApproved += ps.approvedVariations;
    totalPending += ps.pendingVariations;
  }

  // Aggregate monthly summaries from filtered monthly trend
  let thisMonthVariations = 0;
  let lastMonthVariations = 0;
  let yearToDateVariations = 0;
  let thisMonthCount = 0;
  let lastMonthCount = 0;
  let yearToDateCount = 0;
  let thisMonthApproved = 0;
  let thisMonthWaiting = 0;
  let lastMonthWaiting = 0;

  for (const md of monthlyTrend) {
    const submitted = md.submitted || 0;
    const approved = md.approved || 0;
    const pending = md.pending || 0;

    if (md.monthNum === currentMonth && md.year === currentYear) {
      thisMonthVariations = submitted;
      thisMonthCount = approved + pending;
      thisMonthApproved = approved;
      thisMonthWaiting = pending;
    }
    if (md.monthNum === lastMonth && md.year === lastMonthYear) {
      lastMonthVariations = submitted;
      lastMonthCount = approved + pending;
      lastMonthWaiting = pending;
    }
    if (md.year === currentYear && md.monthNum <= currentMonth) {
      yearToDateVariations += submitted;
      yearToDateCount += approved + pending;
    }
  }

  return {
    projectStats,
    monthlyTrend,
    totalVariance,
    totalCredit,
    totalApproved,
    totalPending,
    thisMonthVariations,
    lastMonthVariations,
    yearToDateVariations,
    thisMonthCount,
    lastMonthCount,
    yearToDateCount,
    thisMonthApproved,
    thisMonthWaiting,
    lastMonthWaiting,
  };
}

// ─── Main Hook ───────────────────────────────────────────────────────────────

export function useDashboardFilteredData(
  projects: ProjectWithDetails[],
  variationStats: VariationsStats,
  claimsStatsInput?: ClaimsStatsInput,
): FilteredDashboardData {
  const {
    filters,
    effectiveProjectIds,
    isFiltered,
    filterSummary,
    activeFilterCount,
  } = useDashboardFilter();

  const dateRange = filters.dateRange;

  // ── Step 1: Filter projects ────────────────────────────────────────────────
  const filteredProjects = useMemo(() => {
    if (!effectiveProjectIds) return projects;
    const idSet = new Set(effectiveProjectIds);
    return projects.filter((p) => idSet.has(p.id));
  }, [projects, effectiveProjectIds]);

  const filteredProjectIdSet = useMemo(
    () => new Set(filteredProjects.map((p) => p.id)),
    [filteredProjects],
  );

  // ── Step 2: Filter variation project stats ─────────────────────────────────
  const filteredProjectStats = useMemo(() => {
    if (!isFiltered) return variationStats.projectStats;
    return variationStats.projectStats.filter((ps) =>
      filteredProjectIdSet.has(ps.projectId),
    );
  }, [variationStats.projectStats, filteredProjectIdSet, isFiltered]);

  // ── Step 3: Filter monthly trend data ──────────────────────────────────────
  const filteredMonthlyTrend = useMemo(() => {
    let trend = variationStats.monthlyTrend;

    // Apply date range filter
    if (filters.datePreset !== 'all_time') {
      trend = filterMonthlyVariationData(trend, dateRange);
    }

    return trend;
  }, [variationStats.monthlyTrend, filters.datePreset, dateRange]);

  // ── Step 4: Recalculate aggregates ─────────────────────────────────────────
  const filteredVariationStats = useMemo(() => {
    if (!isFiltered) return variationStats;
    return recalcVariationStats(
      filteredProjectStats,
      filteredMonthlyTrend,
      dateRange,
    );
  }, [
    variationStats,
    filteredProjectStats,
    filteredMonthlyTrend,
    isFiltered,
    dateRange,
  ]);

  // ── Step 5: Compute filtered claims stats ──────────────────────────────────
  const hasDateFilter = filters.datePreset !== 'all_time';

  const filteredClaimsStats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;

    // Contract value from filtered project stats (static, not time-dependent)
    const filteredContractValue = filteredProjectStats.reduce(
      (sum, p) => sum + p.contractValue, 0,
    );

    // Pending count from filtered project stats (current state, not time-dependent)
    const filteredPendingCount = filteredProjectStats.reduce(
      (sum, p) => sum + p.pendingCount, 0,
    );

    let thisMonthClaims = 0;
    let lastMonthClaims = 0;
    let yearToDateClaims = 0;

    // Claim lines that pass every filter; claimsTrendWindow.ts turns them into
    // the Claims Trend points (the last 12 months on All time, otherwise the
    // months in the date filter that have a claim).
    const trendRows: TrendClaimRow[] = [];

    if (claimsStatsInput) {
      const { rawClaimsProgress, subcatToProject } = claimsStatsInput;

      for (const row of rawClaimsProgress) {
        const projectId = subcatToProject[row.subcategoryId];
        // Skip rows not belonging to filtered projects
        if (projectId && !filteredProjectIdSet.has(projectId)) continue;

        // Apply date range filter: skip rows outside the date range
        if (hasDateFilter) {
          if (!isInDateRange(row.claimMonth, row.claimYear, dateRange)) continue;
        }

        // Accumulate into period summary cards
        if (row.claimMonth === currentMonth && row.claimYear === currentYear) {
          thisMonthClaims += row.amount;
        }
        if (row.claimMonth === lastMonth && row.claimYear === lastMonthYear) {
          lastMonthClaims += row.amount;
        }
        if (row.claimYear === currentYear && row.claimMonth <= currentMonth) {
          yearToDateClaims += row.amount;
        }

        trendRows.push(row);
      }
    }

    const filteredMonthlyBreakdown = claimsTrendBreakdown(trendRows, {
      hasDateFilter,
      current: { month: currentMonth, year: currentYear },
    });

    return {
      filteredContractValue,
      filteredPendingCount,
      filteredThisMonthClaims: thisMonthClaims,
      filteredLastMonthClaims: lastMonthClaims,
      filteredYearToDateClaims: yearToDateClaims,
      filteredMonthlyBreakdown,
    };
  }, [filteredProjectStats, filteredProjectIdSet, claimsStatsInput, hasDateFilter, dateRange]);

  // ── Return ─────────────────────────────────────────────────────────────────
  return useMemo(
    () => ({
      filteredProjects,
      filteredProjectIdSet,
      totalProjectCount: projects.length,
      filteredVariationStats,
      filteredProjectStats,
      filteredMonthlyTrend,
      ...filteredClaimsStats,
      isFiltered,
      filterSummary,
      activeFilterCount,
      dateRange,
      datePreset: filters.datePreset,
    }),
    [
      filteredProjects,
      filteredProjectIdSet,
      projects.length,
      filteredVariationStats,
      filteredProjectStats,
      filteredMonthlyTrend,
      filteredClaimsStats,
      isFiltered,
      filterSummary,
      activeFilterCount,
      dateRange,
      filters.datePreset,
    ],
  );
}
